import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { GST_RATES, formatINR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Terminal, Send, Loader2, FileText, Receipt, ArrowRight, MessageSquare, Bot, User, Sparkles, Mic, MicOff, Camera, ImageIcon, Check, Package, Search, BarChart3, ExternalLink, Pencil, Zap, Shield } from "lucide-react";
import { toast } from "sonner";
import HsnCombobox from "@/components/HsnCombobox";

const INITIAL_SUGGESTIONS = [
  "Bill ABC Corp 10000 for consulting",
  "What is the HSN for IT consulting?",
  "Total sales this month?",
  "What are the new TDS rates under Income Tax Act 2025?",
  "Show GSTR-1 report",
];

export default function CommandCenterPage() {
  const [input, setInput] = useState("");
  const [processing, setProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [context, setContext] = useState(null);
  const [listening, setListening] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [quickPicks, setQuickPicks] = useState([]);
  const [sessionId] = useState(() => `cmd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    api.get("/command/quick-picks").then((r) => setQuickPicks(r.data)).catch(() => {});
  }, []);

  /**
   * Pick up the cross-page "Saved ✓" signal that ExpensesPage / PurchasesPage
   * leaves in localStorage after a CC-scan-prefilled save lands. We listen on
   * mount + window focus + storage events so the acknowledgement fires
   * regardless of how the user returns to the chat tab.
   */
  useEffect(() => {
    const consumeSignal = () => {
      try {
        const raw = localStorage.getItem("bizflowa.last_cc_save");
        if (!raw) return;
        const sig = JSON.parse(raw);
        // Discard signals older than 5 minutes — likely stale from a prior session
        if (!sig.at || Date.now() - sig.at > 5 * 60 * 1000) {
          localStorage.removeItem("bizflowa.last_cc_save");
          return;
        }
        localStorage.removeItem("bizflowa.last_cc_save");
        const label = sig.kind === "purchase" ? "Purchase" : "Expense";
        const amountStr = sig.amount ? formatINR(sig.amount) : "";
        const vendor = sig.vendor_name || "Unknown";
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            type: "cc_save_ack",
            text: `Saved as ${label} — ${vendor}${amountStr ? ` · ${amountStr}` : ""}`,
            kind: sig.kind,
          },
        ]);
      } catch { /* ignore malformed JSON */ }
    };
    consumeSignal();
    const onStorage = (e) => {
      if (e.key === "bizflowa.last_cc_save") consumeSignal();
    };
    const onFocus = () => consumeSignal();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const sendMessage = useCallback(async (text, overrideContext) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", text: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setProcessing(true);

    try {
      const payload = { text: text.trim(), session_id: sessionId };
      const ctx = overrideContext !== undefined ? overrideContext : context;
      if (ctx) payload.context = ctx;

      const response = await api.post("/command", payload);
      const data = response.data;

      if (data.action === "follow_up") {
        setContext(data.context);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "follow_up", text: data.question, suggestions: data.suggestions || [] },
        ]);
      } else if (data.action === "confirm_item") {
        setContext(data.context);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "confirm_item", text: data.message, prefill: data.prefill, alternatives: data.alternatives || [], context: data.context },
        ]);
      } else if (data.action === "review_invoice") {
        setContext(data.context);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "review_invoice", text: data.message, review: data.review, context: data.context },
        ]);
      } else if (data.action === "review_expense") {
        setContext(data.context);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "review_expense", text: data.message, review: data.review, context: data.context },
        ]);
      } else if (data.action === "tax_research") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "tax_research", text: data.message, data: data.data },
        ]);
      } else if (data.action === "guardrail") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "guardrail", text: data.message, suggestions: data.suggestions || [] },
        ]);
      } else if (data.action === "query_result") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "query_result", text: data.message, data: data.data },
        ]);
      } else if (data.action === "edit_result") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "edit_result", text: data.message, data: data.data, suggestions: data.suggestions || [] },
        ]);
      } else if (data.action === "report_link") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: "report_link", text: data.message, report: data.report, tab: data.tab },
        ]);
      } else if (data.action === "invoice" || data.action === "expense") {
        setContext(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: data.action, text: data.message, data: data.data },
        ]);
        toast.success(data.message);
        api.get("/command/quick-picks").then((r) => setQuickPicks(r.data)).catch(() => {});
      } else {
        setContext(data.context || null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", type: data.action || "info", text: data.message || data.question || "Something happened.", suggestions: data.suggestions || [] },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", type: "error", text: error.response?.data?.detail || "Something went wrong. Please try again." },
      ]);
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  }, [context, sessionId]);

  const handleNewConversation = () => {
    setMessages([]);
    setContext(null);
    setInput("");
    inputRef.current?.focus();
  };

  const confirmItem = async (itemData, msgContext) => {
    setProcessing(true);
    try {
      const response = await api.post("/command/confirm-item", { 
        item: itemData, 
        context: msgContext,
        session_id: sessionId  // Pass session_id for multi-turn editing
      });
      const data = response.data;
      if (data.action === "follow_up") {
        setContext(data.context);
        setMessages((prev) => [...prev, { role: "assistant", type: "follow_up", text: data.question, suggestions: data.suggestions || [] }]);
      } else if (data.action === "review_invoice") {
        setContext(data.context);
        setMessages((prev) => [...prev, { role: "assistant", type: "review_invoice", text: data.message, review: data.review, context: data.context }]);
      } else if (data.action === "invoice") {
        setContext(null);
        setMessages((prev) => [...prev, { role: "assistant", type: "invoice", text: data.message, data: data.data }]);
        toast.success(data.message);
        api.get("/command/quick-picks").then((r) => setQuickPicks(r.data)).catch(() => {});
      } else {
        setContext(null);
        setMessages((prev) => [...prev, { role: "assistant", type: "info", text: data.message || "Done." }]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm item");
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error("Speech recognition not supported"); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-IN";
    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setListening(false);
      if (text.trim()) {
        sendMessage(text.trim());
      }
    };
    recognition.onerror = () => { setListening(false); toast.error("Voice input failed"); };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20MB");
      e.target.value = "";
      return;
    }
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    const kind = isPdf ? "PDF" : "receipt";
    setScanning(true);
    setMessages((prev) => [...prev, { role: "user", text: `Scanning ${kind}: ${file.name}`, type: "scan_upload" }]);
    setProcessing(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const response = await api.post("/receipt/scan", { image: reader.result });
          const pages = response.data?.page_count || 1;
          const headline = isPdf && pages > 1
            ? `Here's what I found across ${pages} pages of the PDF:`
            : "Here's what I found on the receipt:";
          // Carry the ORIGINAL image data URI through with the OCR result so
          // "Save as Purchase"/"Save as Expense" can hand it to the form,
          // which then ships it to /api/purchases. The backend uploads the
          // raw bytes to the structured Drive folder (`FY_YYYY-YY/Purchases/<Month>`)
          // with the `YYYY-MM-DD_Vendor_Amount.pdf` filename. Without this,
          // the OCR loop succeeds but the invoice never lands in the vault.
          setMessages((prev) => [...prev, {
            role: "assistant",
            type: "scan_result",
            text: headline,
            data: response.data,
            original_image: reader.result,
          }]);
        } catch (err) {
          setMessages((prev) => [...prev, { role: "assistant", type: "error", text: err.response?.data?.detail || `Failed to scan ${kind}` }]);
        } finally { setProcessing(false); setScanning(false); }
      };
      reader.readAsDataURL(file);
    } catch { setProcessing(false); setScanning(false); }
    e.target.value = "";
  };

  const saveScannedExpense = (data, originalImage) => {
    // Pre-fill the Expenses modal so the user can inspect & confirm before saving
    navigate("/expenses", { state: { prefill: { ...data, original_image: originalImage }, source: "command_center_scan" } });
  };

  const saveScannedPurchase = (data, originalImage) => {
    // Pre-fill the Purchases modal — pass the original image data URI so the
    // form ships it to /api/purchases for Drive vault upload.
    navigate("/purchases", { state: { prefill: { ...data, original_image: originalImage }, source: "command_center_scan" } });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="command-center-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4338ca] to-[#6366f1] flex items-center justify-center">
            <Terminal className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-outfit font-semibold text-[#0f172a]">Command Center</h1>
            <p className="text-xs text-[#94a3b8]">Your AI-powered financial butler — invoices, expenses, tax research & compliance</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleNewConversation} data-testid="new-conversation-btn" className="text-xs text-[#64748b]">
          <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> New Chat
        </Button>
      </div>

      {/* Quick Picks Bar */}
      {quickPicks.length > 0 && messages.length === 0 && (
        <div className="px-6 py-3 border-b border-[#e2e8f0] bg-[#fafbfc]" data-testid="quick-picks-bar">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-3.5 w-3.5 text-[#f59e0b]" />
            <span className="text-xs font-medium text-[#64748b]">Frequently Billed</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickPicks.map((pick) => (
              <button
                key={pick.name}
                onClick={() => sendMessage(`Bill for ${pick.name}`)}
                data-testid={`quick-pick-${pick.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#e2e8f0] text-[#334155] hover:border-[#4338ca]/30 hover:bg-[#4338ca]/5 transition-colors font-medium flex items-center gap-1.5"
              >
                <span>{pick.name}</span>
                {pick.rate > 0 && <span className="text-[#94a3b8]">{formatINR(pick.rate)}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#4338ca]/5 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-[#4338ca]" />
            </div>
            <h2 className="text-xl font-outfit font-semibold text-[#0f172a] mb-2">BizFlowa Command Center</h2>
            <p className="text-sm text-[#64748b] mb-6 max-w-md">Create invoices, record expenses, research 2026 GST & Income Tax rules, query your financials — all in plain English.</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {INITIAL_SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => sendMessage(s)} data-testid="initial-suggestion" className="text-xs px-4 py-2 rounded-full bg-[#4338ca]/5 text-[#4338ca] hover:bg-[#4338ca]/15 transition-colors duration-150 font-medium border border-[#4338ca]/10">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <ChatMessage key={i} message={msg} onSuggestionClick={(t) => sendMessage(t)} navigate={navigate} onSaveAsExpense={saveScannedExpense} onSaveAsPurchase={saveScannedPurchase} onConfirmItem={confirmItem} onConfirmReview={(text, ctx) => sendMessage(text, ctx)} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-[#e2e8f0] bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={context ? "Type your response..." : "Try: 'Bill TestCorp 50000 for consulting' or 'Total sales this month?'"}
              disabled={processing}
              data-testid="command-input"
              className="pr-24 h-11 text-sm rounded-xl border-[#e2e8f0] focus-visible:ring-[#4338ca]/30"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={toggleVoice} disabled={processing} data-testid="voice-input-btn" className={`h-7 w-7 p-0 ${listening ? "text-red-500 animate-pulse" : "text-[#94a3b8]"}`}>
                {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} disabled={processing || scanning} data-testid="scan-receipt-btn" className="h-7 w-7 p-0 text-[#94a3b8]">
                {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
              </Button>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleImageUpload} data-testid="cc-upload-input" />
            </div>
          </div>
          <Button onClick={() => sendMessage(input)} disabled={processing || !input.trim()} data-testid="send-command-btn" className="h-11 w-11 p-0 rounded-xl bg-[#4338ca] hover:bg-[#3730a3]">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ message, onSuggestionClick, navigate, onSaveAsExpense, onSaveAsPurchase, onConfirmItem, onConfirmReview }) {
  const isUser = message.role === "user";
  const isError = message.type === "error";
  const isFollowUp = message.type === "follow_up";
  const isInvoice = message.type === "invoice";
  const isExpense = message.type === "expense";
  const isScanResult = message.type === "scan_result";
  const isConfirmItem = message.type === "confirm_item";
  const isReviewInvoice = message.type === "review_invoice";
  const isReviewExpense = message.type === "review_expense";
  const isQueryResult = message.type === "query_result";
  const isReportLink = message.type === "report_link";
  const isTaxResearch = message.type === "tax_research";
  const isGuardrail = message.type === "guardrail";
  const isEditResult = message.type === "edit_result";
  const isSaveAck = message.type === "cc_save_ack";

  if (isSaveAck) {
    const kind = message.kind === "purchase" ? "purchase" : "expense";
    const dest = kind === "purchase" ? "/purchases" : "/expenses";
    return (
      <div className="flex gap-2" data-testid="cc-save-ack-message">
        <div className="w-7 h-7 rounded-full bg-[#10b981]/10 flex items-center justify-center shrink-0 mt-1">
          <Check className="h-4 w-4 text-[#10b981]" />
        </div>
        <div className="max-w-[85%]">
          <div className="rounded-2xl rounded-tl-md bg-[#ecfdf5] border border-[#a7f3d0] px-4 py-3 flex items-center gap-3">
            <span className="text-sm text-[#065f46] font-medium" data-testid="cc-save-ack-text">{message.text}</span>
            <Button size="sm" variant="ghost"
              onClick={() => navigate(dest)}
              data-testid="cc-save-ack-view-btn"
              className="h-7 px-2 text-xs text-[#065f46] hover:text-[#064e3b] hover:bg-[#d1fae5]">
              View <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end gap-2" data-testid="user-message">
        <div className="max-w-[75%]">
          <div className="rounded-2xl rounded-tr-md bg-[#4338ca] text-white px-4 py-3">
            <p className="text-sm">{message.text}</p>
          </div>
        </div>
        <div className="w-7 h-7 rounded-full bg-[#0f172a] flex items-center justify-center shrink-0 mt-1">
          <User className="h-3.5 w-3.5 text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2" data-testid="assistant-message">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-1 ${isTaxResearch ? "bg-[#f59e0b]/10" : isGuardrail ? "bg-red-50" : "bg-[#4338ca]/10"}`}>
        {isTaxResearch ? <Shield className="h-4 w-4 text-[#f59e0b]" /> : isGuardrail ? <Shield className="h-4 w-4 text-red-400" /> : <Bot className="h-4 w-4 text-[#4338ca]" />}
      </div>
      <div className="max-w-[85%] space-y-2">
        <div className={`rounded-2xl rounded-tl-md px-4 py-3 ${isError ? "bg-red-50 border border-red-100" : isTaxResearch ? "bg-[#fffbeb] border border-[#fde68a]" : isGuardrail ? "bg-red-50/50 border border-red-100" : "bg-[#f8fafc] border border-[#e2e8f0]"}`}>
          {isTaxResearch && (
            <div className="flex items-center gap-1.5 mb-2">
              <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px] font-semibold">Tax Research</Badge>
              {message.data?.topic && <span className="text-[10px] text-[#92400e]">{message.data.topic}</span>}
            </div>
          )}
          {isGuardrail && (
            <div className="flex items-center gap-1.5 mb-2">
              <Badge className="bg-red-100 text-red-600 text-[10px] font-semibold">Out of Scope</Badge>
            </div>
          )}
          <div className={`text-sm whitespace-pre-wrap ${isError ? "text-red-700" : isTaxResearch ? "text-[#78350f]" : isGuardrail ? "text-red-700" : "text-[#0f172a]"}`}>
            <FormattedMessage text={message.text} />
          </div>

          {/* Invoice result card */}
          {isInvoice && message.data && (
            <InvoiceResultCard data={message.data} navigate={navigate} />
          )}

          {/* Expense result card */}
          {isExpense && message.data && (
            <ExpenseResultCard data={message.data} />
          )}

          {/* Scan result card */}
          {isScanResult && message.data && (
            <ScanResultCard
              data={message.data}
              originalImage={message.original_image}
              onSaveAsExpense={onSaveAsExpense}
              onSaveAsPurchase={onSaveAsPurchase}
            />
          )}

          {/* Confirm Item validation popup */}
          {isConfirmItem && message.prefill && (
            <ConfirmItemCard prefill={message.prefill} alternatives={message.alternatives} context={message.context} onConfirm={onConfirmItem} />
          )}

          {/* Review Invoice card */}
          {isReviewInvoice && message.review && (
            <ReviewInvoiceCard review={message.review} context={message.context} onConfirm={onConfirmReview} />
          )}

          {/* Review Expense card */}
          {isReviewExpense && message.review && (
            <ReviewExpenseCard review={message.review} context={message.context} onConfirm={onConfirmReview} />
          )}

          {/* Query Result card */}
          {isQueryResult && message.data && (
            <QueryResultCard data={message.data} navigate={navigate} />
          )}

          {/* Report Link card */}
          {isReportLink && (
            <ReportLinkCard report={message.report} navigate={navigate} />
          )}

          {/* Edit Result card */}
          {isEditResult && message.data && (
            <EditResultCard data={message.data} navigate={navigate} />
          )}
        </div>

        {/* Suggestion chips */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="follow-up-suggestions">
            {message.suggestions.map((s) => (
              <button key={s} onClick={() => onSuggestionClick(s)} data-testid="suggestion-chip" className="text-xs px-3 py-1.5 rounded-full bg-[#4338ca]/5 text-[#4338ca] hover:bg-[#4338ca]/15 transition-colors duration-150 font-medium border border-[#4338ca]/10">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceResultCard({ data, navigate }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="invoice-result-card">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-[#4338ca]" />
        <Badge className="bg-[#4338ca] text-white text-xs">{data.status}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#64748b] mb-3">
        <span>Invoice #</span>
        <span className="font-mono text-[#0f172a] font-medium">{data.invoice_number}</span>
        <span>Client</span>
        <span className="text-[#0f172a] font-medium">{data.client_name}</span>
        <span>Subtotal</span>
        <span className="text-[#0f172a]">{formatINR(data.subtotal)}</span>
        {data.cgst_amount > 0 && (<><span>CGST + SGST</span><span className="text-[#0f172a]">{formatINR(data.cgst_amount + data.sgst_amount)}</span></>)}
        {data.igst_amount > 0 && (<><span>IGST</span><span className="text-[#0f172a]">{formatINR(data.igst_amount)}</span></>)}
        <span className="font-semibold text-[#0f172a]">Total</span>
        <span className="font-mono font-bold text-[#4338ca]">{formatINR(data.total)}</span>
      </div>
      <Button size="sm" onClick={() => navigate(`/invoices/${data.invoice_id}/review`)} data-testid="view-invoice-btn" className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg text-xs h-8">
        Review Invoice <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

function ExpenseResultCard({ data }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="expense-result-card">
      <div className="flex items-center gap-2 mb-2">
        <Receipt className="h-4 w-4 text-[#10b981]" />
        <Badge className="bg-[#10b981] text-white text-xs">expense</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#64748b]">
        <span>Vendor</span><span className="text-[#0f172a] font-medium">{data.vendor_name}</span>
        <span>Amount</span><span className="font-mono font-bold text-[#10b981]">{formatINR(data.amount)}</span>
        {data.category && (<><span>Category</span><span className="text-[#0f172a]">{data.category}</span></>)}
        {data.paid_via && (<><span>Paid Via</span><span className="text-[#0f172a]">{data.paid_via}</span></>)}
      </div>
    </div>
  );
}

function ScanResultCard({ data, originalImage, onSaveAsExpense, onSaveAsPurchase }) {
  const itemCount = Array.isArray(data?.line_items) ? data.line_items.length : 0;
  const pageCount = Number(data?.page_count || 1);
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="scan-result-card">
      <div className="flex items-center gap-2 mb-2">
        <Camera className="h-4 w-4 text-[#f59e0b]" />
        <Badge className="bg-[#f59e0b] text-white text-xs">scanned</Badge>
        {pageCount > 1 && (
          <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[10px]">{pageCount} pages</Badge>
        )}
        {itemCount > 0 && (
          <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]">{itemCount} item{itemCount > 1 ? "s" : ""}</Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#64748b] mb-3">
        <span>Vendor</span><span className="text-[#0f172a] font-medium">{data.vendor_name || "-"}</span>
        <span>Amount</span><span className="font-mono font-bold text-[#0f172a]">{formatINR(data.amount)}</span>
        {data.gst_number && (<><span>GSTIN</span><span className="font-mono text-[#0f172a]">{data.gst_number}</span></>)}
        {data.date && (<><span>Date</span><span className="text-[#0f172a]">{data.date}</span></>)}
        {data.category && (<><span>Category</span><span className="text-[#0f172a]">{data.category}</span></>)}
      </div>
      <p className="text-[11px] text-[#64748b] mb-2">Review &amp; confirm in the form before saving:</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onSaveAsPurchase(data, originalImage)} data-testid="save-as-purchase-btn"
          className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg text-xs h-8">
          <FileText className="h-3.5 w-3.5 mr-1.5" /> Save as Purchase
        </Button>
        <Button size="sm" onClick={() => onSaveAsExpense(data, originalImage)} data-testid="save-as-expense-btn"
          className="bg-[#10b981] hover:bg-[#059669] text-white rounded-lg text-xs h-8">
          <Receipt className="h-3.5 w-3.5 mr-1.5" /> Save as Expense
        </Button>
      </div>
    </div>
  );
}

function ReviewInvoiceCard({ review, context, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  if (confirmed) return <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="review-confirmed"><div className="flex items-center gap-2 text-sm text-green-700"><Check className="h-4 w-4" /><span className="font-medium">Invoice confirmed</span></div></div>;
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="review-invoice-card">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-[#4338ca]" />
        <span className="text-sm font-semibold text-[#0f172a]">Invoice Summary</span>
        <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Review</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-[#64748b] mb-3">
        <span>Client</span><span className="text-[#0f172a] font-medium">{review.client_name}</span>
        <span>Item</span><span className="text-[#0f172a] font-medium">{review.description}</span>
        {review.hsn_sac && (<><span>HSN/SAC</span><span className="font-mono text-[#4338ca]">{review.hsn_sac}</span></>)}
        <span>Qty x Rate</span><span className="text-[#0f172a]">{review.quantity} x {formatINR(review.rate)}</span>
        <span>Subtotal</span><span className="text-[#0f172a]">{formatINR(review.amount)}</span>
        <span>GST ({review.gst_rate}%)</span>
        <span className="text-[#0f172a]">
          {review.is_intrastate ? `CGST + SGST: ${formatINR(review.cgst * 2)}` : `IGST: ${formatINR(review.igst)}`}
        </span>
        <span className="font-semibold text-[#0f172a]">Total</span>
        <span className="font-mono font-bold text-[#4338ca]">{formatINR(review.total)}</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { setConfirmed(true); onConfirm("Confirm", context); }} data-testid="confirm-invoice-btn" className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg text-xs h-8 flex-1">
          <Check className="h-3 w-3 mr-1" /> Confirm
        </Button>
        <Button size="sm" variant="outline" onClick={() => onConfirm("Edit", context)} data-testid="edit-invoice-btn" className="rounded-lg text-xs h-8">
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
      </div>
    </div>
  );
}

function ReviewExpenseCard({ review, context, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  if (confirmed) return <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="expense-confirmed"><div className="flex items-center gap-2 text-sm text-green-700"><Check className="h-4 w-4" /><span className="font-medium">Expense confirmed</span></div></div>;
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="review-expense-card">
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="h-4 w-4 text-[#10b981]" />
        <span className="text-sm font-semibold text-[#0f172a]">Expense Summary</span>
        <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Review</Badge>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-[#64748b] mb-3">
        <span>Vendor</span><span className="text-[#0f172a] font-medium">{review.vendor_name}</span>
        <span>Amount</span><span className="font-mono font-bold text-[#10b981]">{formatINR(review.amount)}</span>
        {review.category && (<><span>Category</span><span className="text-[#0f172a]">{review.category}</span></>)}
        {review.paid_via && (<><span>Paid Via</span><span className="text-[#0f172a]">{review.paid_via}</span></>)}
        <span>Date</span><span className="text-[#0f172a]">{review.date}</span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { setConfirmed(true); onConfirm("Confirm", context); }} data-testid="confirm-expense-btn" className="bg-[#10b981] hover:bg-[#059669] text-white rounded-lg text-xs h-8 flex-1">
          <Check className="h-3 w-3 mr-1" /> Confirm
        </Button>
        <Button size="sm" variant="outline" onClick={() => onConfirm("Edit", context)} data-testid="edit-expense-btn" className="rounded-lg text-xs h-8">
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
      </div>
    </div>
  );
}

function QueryResultCard({ data, navigate }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="query-result-card">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-4 w-4 text-[#6366f1]" />
        <Badge className="bg-[#6366f1]/10 text-[#6366f1] text-[10px]">{data.metric || "Query"}</Badge>
      </div>
      {data.items && data.items.length > 0 && (
        <div className="mt-2 space-y-1">
          {data.items.map((item, i) => (
            <div key={i} className="flex justify-between items-center text-xs py-1.5 border-b border-[#f1f5f9] last:border-0">
              <span className="text-[#334155]">{item.vendor || item.name}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-[#0f172a]">{formatINR(item.amount)}</span>
                {item.invoice_id && (
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/invoices/${item.invoice_id}/review`)} className="h-6 px-2 text-[10px] text-[#4338ca] hover:text-[#3730a3] hover:bg-[#4338ca]/5 rounded-md" data-testid={`view-invoice-${i}`}>
                    <FileText className="h-3 w-3 mr-1" />View
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportLinkCard({ report, navigate }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="report-link-card">
      <Button size="sm" onClick={() => navigate("/reports")} data-testid="open-report-btn" className="bg-[#6366f1] hover:bg-[#4f46e5] text-white rounded-lg text-xs h-8">
        <ExternalLink className="h-3 w-3 mr-1.5" /> Open Reports
      </Button>
    </div>
  );
}

function ConfirmItemCard({ prefill, alternatives, context, onConfirm }) {
  const [form, setForm] = useState({
    name: prefill.name || "", hsn_sac: prefill.hsn_sac || "", gst_rate: String(prefill.gst_rate || 18),
    type: prefill.type || "service", unit: prefill.unit || "NOS", rate: prefill.rate || 0,
  });
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hsnError, setHsnError] = useState("");

  const handleConfirm = async () => {
    const cleaned = form.hsn_sac.replace(/\D/g, "");
    if (cleaned.length < 6) { setHsnError("HSN/SAC must be at least 6 digits"); return; }
    setSubmitting(true); setHsnError("");
    await onConfirm({ ...form, hsn_sac: cleaned, gst_rate: Number(form.gst_rate) }, context);
    setConfirmed(true); setSubmitting(false);
  };

  const selectAlternative = (alt) => {
    setForm((prev) => ({ ...prev, hsn_sac: alt.code, gst_rate: String(alt.gst_rate), type: alt.type }));
    setHsnError("");
  };

  if (confirmed) return <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="item-confirmed-card"><div className="flex items-center gap-2 text-sm text-green-700"><Check className="h-4 w-4" /><span className="font-medium">Item added to catalog</span></div></div>;

  const rateLabel = (r) => { const n = Number(r); if (n === 5) return "Merit (5%)"; if (n === 18) return "Standard (18%)"; if (n === 40) return "Luxury (40%)"; return `${n}%`; };

  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="confirm-item-card">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-[#f59e0b]" />
        <span className="text-sm font-semibold text-[#0f172a]">Add New Item to Catalog</span>
        <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Verify & Confirm</Badge>
      </div>
      <div className="space-y-2.5">
        <div>
          <Label className="text-xs font-medium text-[#64748b]">Item / Service Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="confirm-item-name" className="mt-1 h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-medium text-[#64748b]">HSN/SAC Code</Label>
          <div className="mt-1">
            <HsnCombobox value={form.hsn_sac} itemType={form.type === "goods" ? "goods" : "service"} onChange={(code) => { setForm((prev) => ({ ...prev, hsn_sac: code })); setHsnError(""); }} onSelect={(entry) => { if (entry) { setForm((prev) => ({ ...prev, hsn_sac: entry.code, gst_rate: String(entry.gst_rate) })); setHsnError(""); } }} />
          </div>
          {hsnError && <p className="text-xs text-red-500 mt-0.5" data-testid="confirm-hsn-error">{hsnError}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-medium text-[#64748b]">GST Rate</Label>
            <Select value={form.gst_rate} onValueChange={(v) => setForm({ ...form, gst_rate: v })}>
              <SelectTrigger data-testid="confirm-item-gst" className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{rateLabel(r)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#64748b]">Unit (UOM)</Label>
            <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })} data-testid="confirm-item-unit" className="mt-1 h-8 text-sm font-mono uppercase" placeholder="NOS" />
          </div>
        </div>
        {alternatives && alternatives.length > 0 && (
          <div>
            <Label className="text-xs font-medium text-[#64748b] flex items-center gap-1"><Search className="h-3 w-3" /> AI Suggestions</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {alternatives.map((alt) => (
                <button key={alt.code} onClick={() => selectAlternative(alt)} data-testid={`alt-hsn-${alt.code}`} className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${form.hsn_sac === alt.code ? "bg-[#4338ca]/10 border-[#4338ca]/30 text-[#4338ca]" : "bg-[#f8fafc] border-[#e2e8f0] text-[#475569] hover:border-[#4338ca]/20"}`}>
                  <span className="font-mono font-semibold">{alt.code}</span><span className="mx-1">-</span><span>{alt.name}</span>
                  <Badge className={`ml-1.5 text-[9px] px-1 py-0 h-3.5 ${alt.gst_rate === 5 ? "bg-green-100 text-green-700" : alt.gst_rate === 40 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{alt.rate_label}</Badge>
                </button>
              ))}
            </div>
          </div>
        )}
        <Button onClick={handleConfirm} disabled={submitting || !form.name.trim()} data-testid="confirm-add-to-invoice-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg text-xs h-9 mt-1">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
          Confirm & Add to Invoice
        </Button>
      </div>
    </div>
  );
}
function EditResultCard({ data, navigate }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#e2e8f0]" data-testid="edit-result-card">
      <div className="flex items-center gap-2 mb-2">
        <Pencil className="h-4 w-4 text-[#6366f1]" />
        <Badge className="bg-[#6366f1]/10 text-[#6366f1] text-[10px] font-semibold">Invoice Updated</Badge>
        <span className="text-xs text-[#64748b] font-mono">{data.invoice_number}</span>
      </div>
      <div className="space-y-1 mb-3">
        {data.items?.map((item, i) => (
          <div key={i} className="flex justify-between text-xs py-1 border-b border-[#f1f5f9] last:border-0">
            <span className="text-[#334155]">{item.description} <span className="text-[#94a3b8]">x{item.quantity}</span></span>
            <span className="font-mono font-medium text-[#0f172a]">{formatINR(item.amount)}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs mb-3">
        <span className="font-semibold text-[#0f172a]">Grand Total</span>
        <span className="font-mono font-bold text-[#4338ca]">{formatINR(data.grand_total)}</span>
      </div>
      <Button size="sm" onClick={() => navigate(`/invoices/${data.invoice_id}/review`)} data-testid="view-edited-invoice-btn" className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg text-xs h-8">
        View Invoice <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}



function FormattedMessage({ text }) {
  if (!text) return null;
  // Parse **bold** and _italic_ markdown
  const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("_") && part.endsWith("_")) {
      return <em key={i} className="italic opacity-75">{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}
